import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import HistoryIcon from "@mui/icons-material/History";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import BadgeIcon from "@mui/icons-material/Badge";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import VuiBadge from "components/VuiBadge";
import LoadingSpinner from "components/LoadingSpinner";
import { useAuthz } from "context/AuthzContext";
import { useClient } from "context/ClientContext";
import { PERMISSIONS } from "auth/permissions";
import { TC } from "../tokens/text";
import {
  listAllDirectorsForTenant,
  listClientProfiles,
  updateDirector,
  updateDirectorVerification,
  getDirectorHistory,
  uploadDirectorPassport,
} from "services/tenantUserAdminService";

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLES_LIST = [
  { value: "director",   label: "Director"   },
  { value: "secretary",  label: "Secretary"  },
  { value: "llp_member", label: "LLP Member" },
  { value: "other",      label: "Other"      },
];

const VERIFICATION_STATES = [
  { value: "unverified", label: "Unverified", color: TC.secondary, bg: "rgba(255,255,255,0.05)" },
  { value: "pending",    label: "Pending",    color: "#ff9800",               bg: "rgba(255,152,0,0.12)"   },
  { value: "verified",   label: "Verified",   color: "#4caf50",               bg: "rgba(76,175,80,0.12)"   },
  { value: "rejected",   label: "Rejected",   color: "#f44336",               bg: "rgba(244,67,54,0.12)"   },
];

const DIALOG_SX = {
  backgroundImage: "none",
  backgroundColor: "#ffffff",
  color: TC.primary,
  border: "1px solid #d8e6f7",
  borderRadius: "16px",
};

const EMPTY_DIRECTOR = {
  fullName: "", dateOfBirth: "", role: "director", roleRaw: "",
  appointedOn: "", resignedOn: "", nationality: "", occupation: "",
  countryOfResidence: "", notes: "",
  serviceAddress: { addressLine1: "", addressLine2: "", locality: "", region: "", postalCode: "", country: "" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function verificationCfg(state) {
  return VERIFICATION_STATES.find((v) => v.value === state) || VERIFICATION_STATES[0];
}

function VerificationBadge({ state }) {
  const cfg = verificationCfg(state);
  return (
    <Chip
      icon={state === "verified" ? <VerifiedUserIcon sx={{ fontSize: "0.75rem !important" }} /> : undefined}
      label={cfg.label}
      size="small"
      sx={{ bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}`, fontSize: "0.65rem", height: 20, fontWeight: 600 }}
    />
  );
}

function StyledSelect({ value, onChange, children }) {
  return (
    <FormControl fullWidth size="small">
      <Select value={value} onChange={onChange} displayEmpty
        sx={{
          color: TC.primary, fontSize: "0.875rem",
          background: "#f4f8ff",
          border: "1px solid #d8e6f7",
          borderRadius: "8px",
          "& .MuiSelect-icon": { color: TC.secondary },
          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        }}
        MenuProps={{ PaperProps: { sx: {
          background: "rgba(15,18,59,0.98)",
          border: "1px solid #d8e6f7",
          color: TC.primary,
          "& .MuiMenuItem-root:hover": { background: "rgba(0,117,255,0.15)" },
        }}}}
      >
        {children}
      </Select>
    </FormControl>
  );
}

function SectionLabel({ children }) {
  return (
    <VuiTypography variant="caption" fontWeight="bold"
      sx={{ textTransform: "uppercase", letterSpacing: "0.08em", mt: 1, display: "block", mb: 0.5, color: TC.secondary }}>
      {children}
    </VuiTypography>
  );
}

// ─── Director History Dialog ──────────────────────────────────────────────────

function HistoryDialog({ open, onClose, tenantId, director }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !director) return;
    setLoading(true);
    getDirectorHistory(tenantId, director.clientId, director.id)
      .then(({ events: e }) => setEvents(e))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [open, director, tenantId]);

  const EVENT_ICONS = {
    created:                    { label: "Created",              dot: "success" },
    updated:                    { label: "Updated",              dot: "info"    },
    ch_sync_created:            { label: "Imported from CH",     dot: "primary" },
    ch_sync_updated:            { label: "Synced from CH",       dot: "info"    },
    verification_state_changed: { label: "Verification Updated", dot: "warning" },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="director-history-dialog-title"
      aria-describedby="director-history-dialog-description"
      PaperProps={{ sx: DIALOG_SX }}
    >
      <DialogTitle id="director-history-dialog-title" sx={{ color: TC.primary, pb: 0 }}>
        <VuiBox display="flex" alignItems="center" gap={1}>
          <HistoryIcon sx={{ color: TC.linkHover }} />
          <VuiBox>
            <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary }}>Event History</VuiTypography>
            <VuiTypography id="director-history-dialog-description" variant="caption" display="block" sx={{ color: TC.secondary }}>{director?.fullName}</VuiTypography>
          </VuiBox>
        </VuiBox>
      </DialogTitle>
      <DialogContent dividers sx={{ bordercolor: "#7a94b0" }}>
        {loading ? (
          <LoadingSpinner size="sm" label="Loading history…" centered={false} />
        ) : events.length === 0 ? (
          <VuiTypography variant="caption" sx={{ color: TC.secondary }}>No history recorded yet.</VuiTypography>
        ) : (
          <Timeline sx={{ p: 0, m: 0 }}>
            {events.map((ev, i) => {
              const cfg = EVENT_ICONS[ev.type] || { label: ev.type, dot: "grey" };
              return (
                <TimelineItem key={i} sx={{ "&:before": { display: "none" } }}>
                  <TimelineSeparator>
                    <TimelineDot color={cfg.dot} sx={{ width: 8, height: 8, m: 0 }} />
                    {i < events.length - 1 && <TimelineConnector sx={{ bgcolor: "#0a1628" }} />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ pb: 2, pt: 0 }}>
                    <VuiTypography variant="caption" fontWeight="bold" display="block" sx={{ color: TC.primary }}>{cfg.label}</VuiTypography>
                    <VuiTypography variant="caption" display="block" sx={{ color: TC.tertiary,  fontSize: "0.62rem", opacity: 0.7 }}>
                      {ev.timestamp ? new Date(ev.timestamp).toLocaleString("en-GB") : ""}
                    </VuiTypography>
                    {ev.changes?.map((c, ci) => (
                      <VuiTypography key={ci} variant="caption" display="block" sx={{ color: TC.secondary,  fontSize: "0.65rem", mt: 0.3 }}>
                        {c.field}: <span style={{ color: "#f44336", textDecoration: "line-through" }}>{String(c.from ?? "—")}</span>
                        {" → "}
                        <span style={{ color: "#4caf50" }}>{String(c.to ?? "—")}</span>
                      </VuiTypography>
                    ))}
                    {ev.notes && (
                      <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary,  fontSize: "0.65rem", mt: 0.3, fontStyle: "italic" }}>
                        Note: {ev.notes}
                      </VuiTypography>
                    )}
                    {ev.details?.fullName && (
                      <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary,  fontSize: "0.65rem", mt: 0.3 }}>
                        {ev.details.fullName} · {ev.details.role}
                      </VuiTypography>
                    )}
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <VuiButton color="info" size="small" onClick={onClose}>Close</VuiButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Verification Dialog ──────────────────────────────────────────────────────

function VerifyDialog({ open, onClose, tenantId, director, onUpdated }) {
  const [state, setState] = useState("pending");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && director) { setState(director.verificationState || "unverified"); setNotes(""); setError(""); }
  }, [open, director]);

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      const updated = await updateDirectorVerification(tenantId, director.clientId, director.id, state, notes);
      onUpdated(updated);
      onClose();
    } catch (e) {
      setError(e?.message || "Failed to update verification.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: DIALOG_SX }}>
      <DialogTitle sx={{ color: TC.primary }}>Update Verification</DialogTitle>
      <DialogContent dividers sx={{ bordercolor: "#7a94b0" }}>
        <VuiTypography variant="caption" fontWeight="bold" display="block" mb={0.5} sx={{ color: TC.primary }}>{director?.fullName}</VuiTypography>
        <VuiTypography variant="caption" display="block" mb={2} sx={{ color: TC.secondary }}>{director?.clientName}</VuiTypography>
        {error && <VuiTypography color="error" variant="caption" display="block" mb={1}>{error}</VuiTypography>}
        <SectionLabel>Verification State</SectionLabel>
        <StyledSelect value={state} onChange={(e) => setState(e.target.value)}>
          {VERIFICATION_STATES.map((v) => (
            <MenuItem key={v.value} value={v.value} sx={{ fontSize: "0.875rem", color: v.color }}>{v.label}</MenuItem>
          ))}
        </StyledSelect>
        <VuiBox mt={2}>
          <SectionLabel>Notes (optional)</SectionLabel>
          <VuiInput
            multiline minRows={3} maxRows={8}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </VuiBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <VuiButton color="text" size="small" onClick={onClose} disabled={saving}>Cancel</VuiButton>
        <VuiButton color="info" size="small" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Update"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Director Form Dialog (Add / Edit) ────────────────────────────────────────

function DirectorFormDialog({ open, onClose, onSaved, tenantId, existingDirector }) {
  const [form, setForm] = useState(EMPTY_DIRECTOR);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY_DIRECTOR, ...existingDirector });
    setError("");
  }, [open, existingDirector]);

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const setSvcAddr = (key) => (e) => setForm((p) => ({
    ...p, serviceAddress: { ...(p.serviceAddress || {}), [key]: e.target.value },
  }));

  const handleSave = async () => {
    if (!form.fullName.trim()) { setError("Full name is required."); return; }
    setSaving(true); setError("");
    try {
      const result = await updateDirector(tenantId, existingDirector.clientId, existingDirector.id, form);
      onSaved(result);
    } catch (e) {
      setError(e?.message || "Failed to save director.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth PaperProps={{ sx: DIALOG_SX }}>
      <DialogTitle sx={{ color: TC.primary }}>Edit Director</DialogTitle>
      <DialogContent dividers sx={{ bordercolor: "#7a94b0" }}>
        {error && <VuiTypography color="error" variant="caption" display="block" mb={1.5}>{error}</VuiTypography>}

        <SectionLabel>1 · Personal Details</SectionLabel>
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12} md={6}>
            <VuiInput placeholder="Full Name *" value={form.fullName} onChange={set("fullName")} />
          </Grid>
          <Grid item xs={12} md={6}>
            <VuiInput placeholder="Date of Birth (YYYY-MM)" value={form.dateOfBirth || ""} onChange={set("dateOfBirth")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <StyledSelect value={form.role} onChange={set("role")}>
              {ROLES_LIST.map((r) => <MenuItem key={r.value} value={r.value} sx={{ fontSize: "0.875rem" }}>{r.label}</MenuItem>)}
            </StyledSelect>
          </Grid>
          <Grid item xs={12} md={4}>
            <VuiInput placeholder="Role (raw, e.g. corporate-director)" value={form.roleRaw || ""} onChange={set("roleRaw")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <VuiInput placeholder="Occupation" value={form.occupation || ""} onChange={set("occupation")} />
          </Grid>
          <Grid item xs={12} md={6}>
            <VuiInput placeholder="Nationality" value={form.nationality || ""} onChange={set("nationality")} />
          </Grid>
          <Grid item xs={12} md={6}>
            <VuiInput placeholder="Country of Residence" value={form.countryOfResidence || ""} onChange={set("countryOfResidence")} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2, bordercolor: "#7a94b0" }} />
        <SectionLabel>2 · Appointment</SectionLabel>
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12} md={4}>
            <VuiInput placeholder="Appointed On (YYYY-MM-DD)" value={form.appointedOn || ""} onChange={set("appointedOn")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <VuiInput placeholder="Resigned On (YYYY-MM-DD — leave blank if active)" value={form.resignedOn || ""} onChange={set("resignedOn")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <StyledSelect value={form.verificationState || "unverified"} onChange={set("verificationState")}>
              {VERIFICATION_STATES.map((v) => <MenuItem key={v.value} value={v.value} sx={{ fontSize: "0.875rem", color: v.color }}>{v.label}</MenuItem>)}
            </StyledSelect>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2, bordercolor: "#7a94b0" }} />
        <SectionLabel>3 · Service Address</SectionLabel>
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12} md={6}>
            <VuiInput placeholder="Address Line 1" value={form.serviceAddress?.addressLine1 || ""} onChange={setSvcAddr("addressLine1")} />
          </Grid>
          <Grid item xs={12} md={6}>
            <VuiInput placeholder="Address Line 2" value={form.serviceAddress?.addressLine2 || ""} onChange={setSvcAddr("addressLine2")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <VuiInput placeholder="Town / City" value={form.serviceAddress?.locality || ""} onChange={setSvcAddr("locality")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <VuiInput placeholder="Postcode" value={form.serviceAddress?.postalCode || ""} onChange={setSvcAddr("postalCode")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <VuiInput placeholder="Country" value={form.serviceAddress?.country || ""} onChange={setSvcAddr("country")} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2, bordercolor: "#7a94b0" }} />
        <SectionLabel>4 · Notes</SectionLabel>
        <VuiInput multiline minRows={3} maxRows={8} placeholder="Internal notes…" value={form.notes || ""} onChange={set("notes")} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <VuiButton color="text" size="small" onClick={onClose} disabled={saving}>Cancel</VuiButton>
        <VuiButton color="info" size="small" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Changes"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Passport Upload Dialog ───────────────────────────────────────────────────

function PassportDialog({ open, onClose, tenantId, director, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult]       = useState(null); // { extracted, discrepancies, passportUrl }
  const [error, setError]         = useState("");
  const [preview, setPreview]     = useState(null);

  useEffect(() => {
    if (!open) { setResult(null); setError(""); setPreview(null); }
  }, [open]);

  const handleFile = async (file) => {
    if (!file) return;
    setError(""); setResult(null);
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const data = await uploadDirectorPassport(tenantId, director.clientId, director.id, file);
      setResult(data);
      if (onUploaded) onUploaded(data);
    } catch (e) {
      setError(e?.message || "Upload failed.");
    } finally { setUploading(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <Dialog open={open} onClose={() => !uploading && onClose()} maxWidth="sm" fullWidth PaperProps={{ sx: DIALOG_SX }}>
      <DialogTitle sx={{ color: TC.primary, pb: 0 }}>
        <VuiBox display="flex" alignItems="center" gap={1}>
          <BadgeIcon sx={{ color: TC.linkHover }} />
          <VuiBox>
            <VuiTypography sx={{ color: TC.primary }} variant="button" fontWeight="bold">Passport / ID Upload</VuiTypography>
            <VuiTypography variant="caption" sx={{ color: TC.secondary }} display="block">{director?.fullName}</VuiTypography>
          </VuiBox>
        </VuiBox>
      </DialogTitle>

      <DialogContent dividers sx={{ bordercolor: "#7a94b0" }}>
        {/* Upload zone */}
        {!result && (
          <VuiBox
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => !uploading && fileRef.current?.click()}
            sx={{
              border: "2px dashed #0075ff",
              borderRadius: "12px",
              p: 4,
              textAlign: "center",
              cursor: uploading ? "not-allowed" : "pointer",
              background: "rgba(0,117,255,0.04)",
              "&:hover": { background: "rgba(0,117,255,0.09)" },
              mb: 2,
            }}
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              style={{ display: "none" }}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            {uploading ? (
              <VuiBox display="flex" flexDirection="column" alignItems="center" gap={1}>
                <CircularProgress size={32} sx={{ color: TC.linkHover }} />
                <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                  Uploading and scanning with AI…
                </VuiTypography>
              </VuiBox>
            ) : preview ? (
              <VuiBox>
                <img src={preview} alt="passport preview" style={{ maxHeight: 160, borderRadius: 8, marginBottom: 8 }} />
                <VuiTypography variant="caption" sx={{ color: TC.secondary, display: "block" }}>Click or drop to replace</VuiTypography>
              </VuiBox>
            ) : (
              <VuiBox display="flex" flexDirection="column" alignItems="center" gap={1}>
                <UploadFileIcon sx={{ fontSize: "2.5rem", color: TC.linkHover }} />
                <VuiTypography variant="button" sx={{ color: TC.primary, fontWeight: 600 }}>
                  Drop passport image here or click to browse
                </VuiTypography>
                <VuiTypography variant="caption" sx={{ color: TC.tertiary }}>
                  JPG, PNG, PDF — max 10 MB
                </VuiTypography>
              </VuiBox>
            )}
          </VuiBox>
        )}

        {error && (
          <VuiBox mb={2} px={2} py={1.5} sx={{ borderRadius: "10px", background: "#b71c1c", border: "2px solid #c62828" }}>
            <VuiTypography sx={{ color: TC.white, fontSize: "0.85rem", fontWeight: 600 }}>❌ {error}</VuiTypography>
          </VuiBox>
        )}

        {/* Results */}
        {result && (
          <VuiBox>
            {/* Discrepancy banner */}
            {result.discrepancies?.length > 0 ? (
              <VuiBox mb={2} px={2.5} py={2} sx={{ borderRadius: "12px", background: "#e65100", border: "2px solid #f57c00", boxShadow: "0 4px 16px rgba(230,81,0,0.3)" }}>
                <VuiTypography sx={{ color: TC.white, fontSize: "1rem", fontWeight: 700, display: "flex", alignItems: "center", gap: 0.8, mb: 1 }}>
                  <WarningAmberIcon sx={{ fontSize: "1.2rem" }} />
                  {result.discrepancies.length} Discrepanc{result.discrepancies.length === 1 ? "y" : "ies"} Found
                </VuiTypography>
                {result.discrepancies.map((d, i) => (
                  <VuiBox key={i} mb={0.8} px={1.5} py={1} sx={{ background: "rgba(0,0,0,0.2)", borderRadius: "8px" }}>
                    <VuiTypography sx={{ color: "#ffe0b2", fontSize: "0.75rem", fontWeight: 700, display: "block" }}>{d.field}</VuiTypography>
                    <VuiTypography sx={{ color: "#fff3e0", fontSize: "0.78rem", display: "block" }}>
                      Stored: <span style={{ textDecoration: "line-through", opacity: 0.8 }}>{d.stored}</span>
                      {" → "}
                      Passport: <strong>{d.passport}</strong>
                    </VuiTypography>
                  </VuiBox>
                ))}
              </VuiBox>
            ) : (
              <VuiBox mb={2} px={2.5} py={1.5} sx={{ borderRadius: "12px", background: "#1b5e20", border: "2px solid #2e7d32", boxShadow: "0 4px 16px rgba(27,94,32,0.3)" }}>
                <VuiTypography sx={{ color: TC.white, fontSize: "0.95rem", fontWeight: 700, display: "flex", alignItems: "center", gap: 0.8 }}>
                  <CheckCircleIcon sx={{ fontSize: "1.1rem" }} />
                  All details match — no discrepancies found
                </VuiTypography>
              </VuiBox>
            )}

            {/* Extracted data table */}
            <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary, textTransform: "uppercase", fontSize: "0.62rem", letterSpacing: "0.08em", display: "block", mb: 1 }}>
              Extracted from Document
            </VuiTypography>
            <VuiBox px={1.5} py={1} sx={{ background: "#f4f8ff", borderRadius: "10px", border: "1px solid #d8e6f7" }}>
              {[
                { label: "Document Type",  value: result.extracted?.documentType },
                { label: "Full Legal Name", value: result.extracted?.fullLegalName },
                { label: "Date of Birth",   value: result.extracted?.dateOfBirth },
                { label: "Nationality",     value: result.extracted?.nationality },
                { label: "Document No.",    value: result.extracted?.documentNumber },
                { label: "Expiry Date",     value: result.extracted?.expiryDate },
                { label: "Gender",          value: result.extracted?.gender },
                { label: "NI Number",       value: result.extracted?.niNumber },
              ].filter(f => f.value).map(f => (
                <VuiBox key={f.label} display="flex" justifyContent="space-between" py={0.5} sx={{ borderBottom: "1px solid #e4eef8", "&:last-child": { borderBottom: "none" } }}>
                  <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.75rem" }}>{f.label}</VuiTypography>
                  <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.75rem", fontWeight: 600 }}>{f.value}</VuiTypography>
                </VuiBox>
              ))}
            </VuiBox>

            {result.extracted?.confidence != null && (
              <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: "0.65rem", display: "block", mt: 1, textAlign: "right" }}>
                AI confidence: {Math.round(result.extracted.confidence * 100)}%
              </VuiTypography>
            )}

            <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: "0.65rem", display: "block", mt: 0.5 }}>
              ✓ Passport data saved to this director record
              {!result.passportUrl && " (image not stored — storage unavailable)"}
            </VuiTypography>

            {/* Allow re-upload */}
            <VuiBox mt={2}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => !uploading && fileRef.current?.click()}
              sx={{ border: "1px dashed #0075ff", borderRadius: "8px", p: 1.5, textAlign: "center", cursor: "pointer", background: "rgba(0,117,255,0.03)" }}
            >
              <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={(e) => handleFile(e.target.files?.[0])} />
              <VuiTypography variant="caption" sx={{ color: TC.linkHover, fontSize: "0.72rem" }}>↑ Upload a different image</VuiTypography>
            </VuiBox>
          </VuiBox>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <VuiButton color="info" size="small" onClick={onClose}>Close</VuiButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Director Card ────────────────────────────────────────────────────────────

function DirectorCard({ director, onEdit, onVerify, onHistory, onPassport, onSelect }) {
  const isResigned = director.isActive === false || (!director.isActive && Boolean(director.resignedOn));
  const cfg = verificationCfg(director.verificationState || "unverified");

  return (
    <VuiBox
      p={2}
      onClick={onSelect}
      sx={{
        background: isResigned ? "rgba(255,255,255,0.02)" : "rgba(0,20,60,0.6)",
        border: `1px solid ${isResigned ? "rgba(255,255,255,0.07)" : "rgba(0,117,255,0.2)"}`,
        borderRadius: "12px",
        opacity: isResigned ? 0.65 : 1,
        transition: "border-color 0.2s, box-shadow 0.2s",
        cursor: "pointer",
        "&:hover": {
          borderColor: isResigned ? "rgba(255,255,255,0.2)" : "rgba(0,117,255,0.6)",
          boxShadow: "0 4px 20px rgba(0,117,255,0.15)",
        },
      }}
    >
      {/* Header */}
      <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <VuiBox display="flex" alignItems="center" gap={1}>
          <VuiBox
            sx={{
              width: 36, height: 36, borderRadius: "50%",
              background: isResigned ? "rgba(255,255,255,0.06)" : "rgba(0,117,255,0.15)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <PersonIcon sx={{ color: isResigned ? "rgba(255,255,255,0.3)" : "#0075ff", fontSize: "1.1rem" }} />
          </VuiBox>
          <VuiBox>
            <VuiTypography variant="button" color={isResigned ? "text" : "white"} fontWeight="bold" display="block">
              {director.fullName}
            </VuiTypography>
            <VuiBox display="flex" gap={0.5} flexWrap="wrap" mt={0.2}>
              <Chip label={director.role?.replace(/_/g, " ") || "Director"} size="small"
                sx={{ bgcolor: "rgba(0,117,255,0.15)", color: TC.link, fontSize: "0.6rem", height: 16 }} />
              {director.source === "companies_house" && (
                <Chip label="CH" size="small"
                  sx={{ bgcolor: "rgba(76,175,80,0.12)", color: "#4caf50", border: "1px solid rgba(76,175,80,0.3)", fontSize: "0.6rem", height: 16 }} />
              )}
              {isResigned && (
                <Chip label="Resigned" size="small"
                  sx={{ bgcolor: "rgba(244,67,54,0.12)", color: "#f44336", border: "1px solid rgba(244,67,54,0.3)", fontSize: "0.6rem", height: 16 }} />
              )}
            </VuiBox>
          </VuiBox>
        </VuiBox>
        <VerificationBadge state={director.verificationState || "unverified"} />
      </VuiBox>

      {/* Companies */}
      <VuiBox mb={1}>
        {(director.companies || [{ clientId: director.clientId, clientName: director.clientName, companyRegNumber: director.companyRegNumber, resignedOn: director.resignedOn }]).map((c, i) => (
          <VuiBox key={i} display="flex" alignItems="center" gap={0.5} mb={0.3}>
            <BusinessIcon sx={{ fontSize: "0.8rem", color: TC.secondary, flexShrink: 0 }} />
            <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: "0.72rem" }}>
              {c.clientName || c.clientId}
              {c.companyRegNumber && <span style={{ opacity: 0.5 }}> · {c.companyRegNumber}</span>}
              {c.resignedOn && <span style={{ color: "#f44336", marginLeft: 4 }}>(resigned)</span>}
            </VuiTypography>
          </VuiBox>
        ))}
      </VuiBox>

      {/* Details grid */}
      <Grid container spacing={0.5} mb={1.5}>
        {[
          { label: "Appointed",   value: director.appointedOn },
          { label: "Resigned",    value: director.resignedOn },
          { label: "DOB",         value: director.dateOfBirth },
          { label: "Nationality", value: director.nationality },
          { label: "Occupation",  value: director.occupation },
          { label: "Residence",   value: director.countryOfResidence },
        ].filter(f => f.value).map(f => (
          <Grid item xs={6} key={f.label}>
            <VuiTypography variant="caption" display="block" sx={{ fontSize: "0.58rem", opacity: 0.6, lineHeight: 1.3, color: "rgba(255,255,255,0.5)" }}>{f.label}</VuiTypography>
            <VuiTypography variant="caption" sx={{ color: TC.white, fontSize: "0.72rem", lineHeight: 1.3 }}>{f.value}</VuiTypography>
          </Grid>
        ))}
      </Grid>

      {/* Service address */}
      {director.serviceAddress?.postalCode && (
        <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.62rem", opacity: 0.5, display: "block", mb: 1 }}>
          📮 {[director.serviceAddress.addressLine1, director.serviceAddress.locality, director.serviceAddress.postalCode].filter(Boolean).join(", ")}
        </VuiTypography>
      )}

      {/* Notes */}
      {director.notes && (
        <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.65rem", fontStyle: "italic", display: "block", mb: 1, opacity: 0.7 }}>
          {director.notes}
        </VuiTypography>
      )}

      {/* Actions */}
      <VuiBox display="flex" gap={0.8} mt={0.5} flexWrap="wrap">
        <VuiButton color="info" size="small" variant="outlined"
          onClick={(e) => { e.stopPropagation(); onEdit(director); }}
          sx={{ flex: 1, fontSize: "0.65rem", py: 0.4, px: 0.8 }}>Edit</VuiButton>
        <VuiButton color="warning" size="small" variant="outlined"
          onClick={(e) => { e.stopPropagation(); onVerify(director); }}
          sx={{ flex: 1, fontSize: "0.65rem", py: 0.4, px: 0.8 }}>Verify</VuiButton>
        <VuiButton color="text" size="small" variant="outlined"
          onClick={(e) => { e.stopPropagation(); onHistory(director); }}
          sx={{ flex: 1, fontSize: "0.65rem", py: 0.4, px: 0.8 }}>History</VuiButton>
        <VuiButton color="secondary" size="small" variant="outlined"
          onClick={(e) => { e.stopPropagation(); onPassport(director); }}
          sx={{ flex: 1, fontSize: "0.65rem", py: 0.4, px: 0.8, minWidth: 70 }}>
          <BadgeIcon sx={{ fontSize: "0.9rem", mr: 0.4 }} />Passport
        </VuiButton>
      </VuiBox>

      {/* Passport uploaded indicator */}
      {director.passportUploadedAt && (
        <VuiTypography variant="caption" sx={{ color: "#4caf50", fontSize: "0.6rem", display: "block", mt: 0.8 }}>
          🪪 Passport on file · {new Date(director.passportUploadedAt).toLocaleDateString("en-GB")}
          {director.passportDiscrepancies?.length > 0 && (
            <span style={{ color: "#ff9800", marginLeft: 6 }}>
              ⚠ {director.passportDiscrepancies.length} discrepanc{director.passportDiscrepancies.length === 1 ? "y" : "ies"}
            </span>
          )}
        </VuiTypography>
      )}
    </VuiBox>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Directors() {
  const { tenantId, isSuperAdmin, isTenantAdmin, hasAllPermissions } = useAuthz();
  const { pinnedClientId, pinnedClientName, clearPin } = useClient();
  const canManage = isSuperAdmin || isTenantAdmin || hasAllPermissions([PERMISSIONS.MANAGE_CLIENT_PROFILES]);
  const location = useLocation();
  const history = useHistory();

  // URL param takes priority; fall back to pinned client from context
  const queryClientId = useMemo(() => new URLSearchParams(location.search).get("clientId") || "", [location.search]);

  const [directors, setDirectors] = useState([]);
  const [clientProfiles, setClientProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState(queryClientId || pinnedClientId);
  const [roleFilter, setRoleFilter] = useState("");
  const [verifyFilter, setVerifyFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [successMsg, setSuccessMsg] = useState("");

  const [formOpen, setFormOpen]         = useState(false);
  const [editDirector, setEditDirector] = useState(null);
  const [historyDirector, setHistoryDirector] = useState(null);
  const [verifyDirector,  setVerifyDirector]  = useState(null);
  const [passportDirector, setPassportDirector] = useState(null);

  const flash = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(""), 4000); };

  const load = async () => {
    if (!tenantId) return;
    setLoading(true); setError("");
    try {
      const [dirData, profData] = await Promise.all([
        listAllDirectorsForTenant(tenantId),
        listClientProfiles({ tenantId }),
      ]);
      setDirectors(dirData.directors);
      setClientProfiles(profData.clientProfiles || []);
    } catch (e) {
      setError(e?.message || "Failed to load directors.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tenantId]);

  // Sync clientFilter when URL param changes; fall back to pinned client
  useEffect(() => {
    setClientFilter(queryClientId || pinnedClientId);
  }, [queryClientId, pinnedClientId]);

  const filtered = useMemo(() => {
    let list = directors;
    if (clientFilter) list = list.filter((d) => d.clientId === clientFilter);
    if (statusFilter === "active")   list = list.filter((d) => !d.resignedOn);
    if (statusFilter === "resigned") list = list.filter((d) =>  d.resignedOn);
    if (roleFilter)   list = list.filter((d) => d.role === roleFilter);
    if (verifyFilter) list = list.filter((d) => (d.verificationState || "unverified") === verifyFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((d) =>
        (d.fullName     || "").toLowerCase().includes(q) ||
        (d.clientName   || "").toLowerCase().includes(q) ||
        (d.nationality  || "").toLowerCase().includes(q) ||
        (d.occupation   || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [directors, clientFilter, statusFilter, roleFilter, verifyFilter, search]);

  // Stats
  const stats = useMemo(() => ({
    total:      directors.length,
    active:     directors.filter((d) => !d.resignedOn).length,
    resigned:   directors.filter((d) =>  d.resignedOn).length,
    unverified: directors.filter((d) => !d.verificationState || d.verificationState === "unverified").length,
    verified:   directors.filter((d) =>  d.verificationState === "verified").length,
    fromCH:     directors.filter((d) =>  d.source === "companies_house").length,
  }), [directors]);

  const handleSaved = (director) => {
    setFormOpen(false); setEditDirector(null);
    flash(`Director "${director.fullName}" saved.`);
    load();
  };

  const handleVerifyUpdated = (updated) => {
    setDirectors((prev) => prev.map((d) =>
      d.id === updated.id && d.clientId === updated.clientId ? { ...d, ...updated } : d
    ));
    flash(`Verification updated for ${updated.fullName}.`);
  };

  if (!canManage) {
    return (
      <DashboardLayout><DashboardNavbar />
        <VuiBox py={3}><VuiTypography color="error" variant="button">You do not have permission to manage directors.</VuiTypography></VuiBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>

        {/* Header */}
        <VuiBox mb={3} display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <VuiBox>
            <VuiTypography variant="lg" fontWeight="bold" sx={{ color: TC.primary }}>Directors &amp; Officers</VuiTypography>
            <VuiTypography variant="caption" display="block" mt={0.5} sx={{ color: TC.secondary }}>
              First-class director identities — appointment timeline, verification state, and change history.
            Directors are created automatically via Companies House sync from a company client profile.
          </VuiTypography>
          </VuiBox>
        </VuiBox>

        {successMsg && <VuiTypography color="success" variant="button" mb={2} display="block">{successMsg}</VuiTypography>}
        {error       && <VuiTypography color="error"   variant="button" mb={2} display="block">{error}</VuiTypography>}

        {/* Company filter banner */}
        {clientFilter && (() => {
          const cp = clientProfiles.find((p) => p.id === clientFilter);
          const isPinned = pinnedClientId === clientFilter;
          return (
            <VuiBox mb={2} px={2} py={1} borderRadius="8px"
              display="flex" alignItems="center" justifyContent="space-between"
              sx={{ background: isPinned ? "rgba(255,179,0,0.1)" : "rgba(0,117,255,0.12)", border: `1px solid ${isPinned ? "rgba(255,179,0,0.35)" : "rgba(0,117,255,0.3)"}` }}>
              <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                {isPinned ? "📌" : "🏢"} Showing directors for: <strong>{cp?.clientName || cp?.legalName || pinnedClientName || clientFilter}</strong>
                {cp?.companyRegNumber && <span style={{ opacity: 0.6 }}> ({cp.companyRegNumber})</span>}
                {isPinned && <span style={{ opacity: 0.5, marginLeft: 8 }}>— pinned from Client Profiles</span>}
              </VuiTypography>
              <VuiButton size="small" variant="outlined" color="text"
                sx={{ fontSize: "0.7rem", py: 0.3, px: 1 }}
                onClick={() => { setClientFilter(""); if (isPinned) clearPin(); history.push("/directors"); }}>
                ✕ Clear filter
              </VuiButton>
            </VuiBox>
          );
        })()}

        {/* Stats bar */}
        <Grid container spacing={2} mb={3}>
          {[
            { label: "Total",       value: stats.total,      color: TC.primary   },
            { label: "Active",      value: stats.active,     color: "#4caf50" },
            { label: "Resigned",    value: stats.resigned,   color: "#f44336" },
            { label: "Unverified",  value: stats.unverified, color: "#ff9800" },
            { label: "Verified",    value: stats.verified,   color: "#4caf50" },
            { label: "From CH",     value: stats.fromCH,     color: TC.linkHover },
          ].map((s) => (
            <Grid item xs={6} sm={4} md={2} key={s.label}>
              <Card sx={{ textAlign: "center", py: 1.5 }}>
                <VuiTypography variant="h5" sx={{ color: s.color, fontWeight: 700 }}>{s.value}</VuiTypography>
                <VuiTypography variant="caption" sx={{ color: TC.secondary }}>{s.label}</VuiTypography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Filter toolbar */}
        <Card sx={{ mb: 3 }}>
          <VuiBox p={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <VuiBox sx={{ flex: 1, minWidth: 200 }}>
              <VuiInput placeholder="Search name, company, nationality…" value={search}
                onChange={(e) => setSearch(e.target.value)} icon={{ component: <SearchIcon />, direction: "left" }} />
            </VuiBox>
            <VuiBox sx={{ minWidth: 140 }}>
              <StyledSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value=""         sx={{ fontSize: "0.875rem" }}>All Status</MenuItem>
                <MenuItem value="active"   sx={{ fontSize: "0.875rem" }}>Active</MenuItem>
                <MenuItem value="resigned" sx={{ fontSize: "0.875rem" }}>Resigned</MenuItem>
              </StyledSelect>
            </VuiBox>
            <VuiBox sx={{ minWidth: 140 }}>
              <StyledSelect value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                <MenuItem value="" sx={{ fontSize: "0.875rem" }}>All Roles</MenuItem>
                {ROLES_LIST.map((r) => <MenuItem key={r.value} value={r.value} sx={{ fontSize: "0.875rem" }}>{r.label}</MenuItem>)}
              </StyledSelect>
            </VuiBox>
            <VuiBox sx={{ minWidth: 140 }}>
              <StyledSelect value={verifyFilter} onChange={(e) => setVerifyFilter(e.target.value)}>
                <MenuItem value="" sx={{ fontSize: "0.875rem" }}>All Verification</MenuItem>
                {VERIFICATION_STATES.map((v) => (
                  <MenuItem key={v.value} value={v.value} sx={{ fontSize: "0.875rem", color: v.color }}>{v.label}</MenuItem>
                ))}
              </StyledSelect>
            </VuiBox>
            {clientProfiles.length > 0 && (
              <VuiBox sx={{ minWidth: 180 }}>
                <StyledSelect
                  value={clientFilter}
                  onChange={(e) => {
                    const val = e.target.value;
                    setClientFilter(val);
                    history.push(val ? `/directors?clientId=${val}` : "/directors");
                  }}
                  displayEmpty
                >
                  <MenuItem value="" sx={{ fontSize: "0.875rem" }}>All Companies</MenuItem>
                  {clientProfiles.map((p) => (
                    <MenuItem key={p.id} value={p.id} sx={{ fontSize: "0.875rem" }}>
                      {p.clientName || p.id}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </VuiBox>
            )}
            <VuiTypography variant="caption" sx={{ color: TC.secondary,  ml: "auto" }}>
              {filtered.length} director{filtered.length !== 1 ? "s" : ""}
            </VuiTypography>
          </VuiBox>
        </Card>

        {/* Director cards grid */}
        {loading ? (
          <LoadingSpinner label="Loading directors…" />
        ) : filtered.length === 0 ? (
          <VuiBox py={5} textAlign="center">
            <PersonIcon sx={{ fontSize: 48, color: TC.tertiary, mb: 1 }} />
            <VuiTypography variant="button" display="block" sx={{ color: TC.primary }}>
              {directors.length === 0
                ? "No directors yet. Import from a client profile via Companies House sync."
                : "No directors match your filters."}
            </VuiTypography>
          </VuiBox>
        ) : (
          <Grid container spacing={2}>
            {filtered.map((d) => (
              <Grid item xs={12} sm={6} md={4} key={`${d.clientId}-${d.id}`}>
                <DirectorCard
                  director={d}
                  onSelect={() => {
                    if (d.linkedClientId) {
                      history.push(`/individual-clients/${d.linkedClientId}`);
                    } else {
                      history.push(`/client-profiles/${d.clientId}`);
                    }
                  }}
                  onEdit={(dir)    => { setEditDirector(dir); setFormOpen(true);   }}
                  onVerify={(dir)  => setVerifyDirector(dir)}
                  onHistory={(dir) => setHistoryDirector(dir)}
                  onPassport={(dir) => setPassportDirector(dir)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </VuiBox>

      {/* Dialogs */}
      <DirectorFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditDirector(null); }}
        onSaved={handleSaved}
        tenantId={tenantId}
        existingDirector={editDirector}
      />
      <HistoryDialog
        open={Boolean(historyDirector)}
        onClose={() => setHistoryDirector(null)}
        tenantId={tenantId}
        director={historyDirector}
      />
      <VerifyDialog
        open={Boolean(verifyDirector)}
        onClose={() => setVerifyDirector(null)}
        tenantId={tenantId}
        director={verifyDirector}
        onUpdated={handleVerifyUpdated}
      />
      <PassportDialog
        open={Boolean(passportDirector)}
        onClose={() => setPassportDirector(null)}
        tenantId={tenantId}
        director={passportDirector}
        onUploaded={(data) => {
          // Update director in local state with passport fields
          setDirectors((prev) => prev.map((d) =>
            d.id === passportDirector?.id && d.clientId === passportDirector?.clientId
              ? { ...d, passportUploadedAt: new Date().toISOString(), passportDiscrepancies: data.discrepancies }
              : d
          ));
          flash(`Passport uploaded for ${passportDirector?.fullName}.${data.discrepancies?.length > 0 ? ` ⚠ ${data.discrepancies.length} discrepanc${data.discrepancies.length === 1 ? "y" : "ies"} found.` : " No discrepancies."}`);
        }}
      />
      <Footer />
    </DashboardLayout>
  );
}

